import { Badge, Spinner } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { TrackingStatus, useShipmentControllerTrackShipment } from '../../Types/platform-api-gen';

interface ShipmentTrackerChipProps {
    trackingNumber: string | null | undefined;
    carrier: string | null | undefined;
}

function getStatusConfig(status: TrackingStatus | undefined, error: boolean): { variant: string; icon: string } {
    if (error) {
        return { variant: 'danger', icon: 'bi-exclamation-triangle' };
    }

    switch (status) {
        case TrackingStatus.Delivered:
            return { variant: 'success', icon: 'bi-check-circle' };
        case TrackingStatus.Out_for_delivery:
            return { variant: 'info', icon: 'bi-truck' };
        case TrackingStatus.In_transit:
            return { variant: 'primary', icon: 'bi-box-seam' };
        case TrackingStatus.Picked_up:
            return { variant: 'primary', icon: 'bi-box-arrow-up' };
        case TrackingStatus.Opened:
            return { variant: 'secondary', icon: 'bi-envelope-open' };
        case TrackingStatus.Exception:
            return { variant: 'danger', icon: 'bi-exclamation-circle' };
        case TrackingStatus.Cancelled:
            return { variant: 'secondary', icon: 'bi-x-circle' };
        default:
            return { variant: 'secondary', icon: 'bi-question-circle' };
    }
}

function formatFutureDate(dateStr: string | null | undefined): string | null {
    if (!dateStr) return null;
    const date = DateTime.fromISO(dateStr);
    if (!date.isValid) return null;

    const now = DateTime.now();
    const diffDays = Math.floor(date.startOf('day').diff(now.startOf('day'), 'days').days);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays >= 2 && diffDays < 7) return `in ${diffDays} days`;
    return date.toFormat('MMM d');
}

function formatPastDate(dateStr: string | null | undefined): string | null {
    if (!dateStr) return null;
    const date = DateTime.fromISO(dateStr);
    if (!date.isValid) return null;

    const now = DateTime.now();
    const diffDays = Math.floor(now.startOf('day').diff(date.startOf('day'), 'days').days);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays >= 2 && diffDays < 7) return `${diffDays} days ago`;
    return date.toFormat('MMM d');
}

export function ShipmentTrackerChip({ trackingNumber, carrier }: ShipmentTrackerChipProps) {
    const {
        data: shipment,
        isLoading,
        error
    } = useShipmentControllerTrackShipment(
        {
            trackingNumber: trackingNumber ?? '',
            carrier: carrier ?? undefined
        },
        {
            query: {
                queryKey: ['track-shipment', trackingNumber],
                enabled: !!trackingNumber
            }
        }
    );

    console.log('ShipmentTrackerChip', { trackingNumber, carrier, shipment, isLoading, error });

    // No tracking number provided
    if (!trackingNumber) {
        return (
            <Badge
                bg="light"
                text="dark"
                style={{
                    fontSize: '0.7rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    border: '1px solid #dee2e6'
                }}
                title="No tracking information available"
            >
                <i className="bi-slash-circle" style={{ fontSize: '0.65rem' }} />
                <span>No tracking</span>
            </Badge>
        );
    }

    const config = getStatusConfig(shipment?.status as TrackingStatus | undefined, !!error);
    const shortTracking = trackingNumber;
    const isDelivered = shipment?.status === TrackingStatus.Delivered;
    const eta = isDelivered ? null : formatFutureDate(shipment?.estimatedDelivery);
    const deliveredDate = isDelivered ? formatPastDate(shipment?.deliveryDate) : null;

    const handleClick = () => {
        if (shipment?.trackingLink) {
            window.open(shipment.trackingLink, '_blank');
        }
    };

    return (
        <Badge
            bg={isLoading ? 'secondary' : config.variant}
            style={{
                fontSize: '0.7rem',
                color: '#fff',
                cursor: shipment?.trackingLink ? 'pointer' : 'default',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem'
            }}
            onClick={handleClick}
            title={shipment?.trackingLink ? `Track: ${trackingNumber}` : trackingNumber}
        >
            {isLoading ? (
                <>
                    <Spinner animation="border" size="sm" style={{ width: 10, height: 10, borderWidth: 1 }} />
                    <span>{shortTracking}</span>
                </>
            ) : (
                <>
                    <i className={config.icon} style={{ fontSize: '0.65rem' }} />
                    <span>{shortTracking}</span>
                    {eta && <span className="opacity-75">· ETA {eta}</span>}
                    {deliveredDate && <span className="opacity-75">· {deliveredDate}</span>}
                </>
            )}
        </Badge>
    );
}
